import { AppData, Language, Registry } from '@lightningjs/sdk';
import { constants } from 'aliases';
import { AbstractDeviceIntegration } from 'config/platforms/AbstractDeviceIntegration';
import jwtDecode from 'jwt-decode';
import {
  ensureOneTrustAboveCanvas,
  getOneTrustSdkPath,
  loadOneTrustScript,
} from 'libraries/oneTrustSdk';
import {
  ErrorCode,
  reportError,
} from 'services/analytics/reportingServicePage';
import BackgroundingService from 'services/BackgroundingService';
import { getConfig, getOneTrustJwt } from 'services/cwService';
import PrivacyService from 'services/PrivacyService';
import { generateUUIDv4 } from 'support/generalUtils';
import { JwtClaims } from 'support/jwtUtils';
import {
  OneTrustDialogStatusDetail,
  OneTrustHeaders,
  OneTrustPurposeConsent,
  OneTrustPurposeStatus,
  OneTrustSdk,
} from 'types/oneTrust';

export type OneTrustConfig = {
  appId: string;
  cdnLocation: string;
  deviceType: string;
  forceBanner: boolean;
  sdkVersion: string;
};

export type OneTrustJwtStorage = {
  jwt: string;
  uid: string;
  kid: string;
  generatedAt: number; // ms epoch
};

type OneTrustServiceParams = {
  config: OneTrustConfig;
  deviceIntegration: AbstractDeviceIntegration;
  privacyService: PrivacyService;
};

const LOG_PREFIX = '[OneTrust]';
const DEFAULT_LANGUAGE = 'en';
const STATUS_OK = 200;
const DIALOG_STATUS_EVENT = 'oneTrust:UiDialogStatusChange';
const TARGETING_PURPOSE_ID = 'C0004';
const US_PRIVACY_STORAGE_KEY = 'IABUSPrivacy_String';
const GPP_STRING_STORAGE_KEY = 'IABGPP_HDR_GppString';
const GPP_SID_STORAGE_KEY = 'IABGPP_GppSID';
const CONSENT_STRING_STORAGE_KEY = 'otConsentString';
const SAVE_POLL_INTERVAL_MS = 250;
const FETCH_TYPE_SYNC_PROFILE = 'APP_DATA_AND_SYNC_PROFILE';
const JWT_EXPIRY_BUFFER_MS = 60 * 1000;
export default class OneTrustService {
  static readonly serviceName = 'OneTrustService';

  private readonly config: OneTrustConfig;
  private readonly deviceIntegration: AbstractDeviceIntegration;
  private readonly privacyService: PrivacyService;

  private sdk: OneTrustSdk | null = null;

  // Tracked separately from the SDK so a login before `init()` still counts
  private currentUid: string | null = null;
  // Bumped per (re)start so a slower, older restart can't clobber a newer one
  private userSequence = 0;

  private dialogOpen = false;
  private hasDialogOpened = false;
  private dialogTimeoutId: ReturnType<typeof Registry.setTimeout> | null = null;
  private onBannerClosed: (() => void) | null = null;

  private isBackgrounded = false;
  private isStarting = false;
  private nextRefetchAt: number | null = null;
  private refetchPending = false;
  private refetchTimeoutId: ReturnType<typeof Registry.setTimeout> | null =
    null;

  constructor(params: OneTrustServiceParams) {
    const { config, deviceIntegration, privacyService } = params;

    this.config = config;
    this.deviceIntegration = deviceIntegration;
    this.privacyService = privacyService;
  }

  get isInitialized(): boolean {
    return !!this.sdk;
  }

  /**
   * True while a OneTrust dialog (banner or preference center) is on screen
   */
  get isDialogOpen(): boolean {
    return this.dialogOpen;
  }

  private getHeaders(): OneTrustHeaders | null {
    const { appId, cdnLocation, sdkVersion, deviceType, forceBanner } =
      this.config;

    if (!appId || !cdnLocation || !sdkVersion) {
      AppData?.log.debug(`${LOG_PREFIX} missing configuration, skipping init`);
      return null;
    }

    return {
      'OT-CDN-Location': cdnLocation,
      'OT-APP-Id': appId,
      'OT-Language': Language.get() || DEFAULT_LANGUAGE,
      'OT-SDK-Version': sdkVersion,
      'OT-Device-Type': deviceType,
      ...(forceBanner && { 'OT-Force-Fetch': 'true' }),
    };
  }

  private startSdk(sdk: OneTrustSdk, headers: OneTrustHeaders): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      sdk.startSDK(headers, (status, response, error) => {
        if (status !== STATUS_OK || error) {
          reject(error ?? new Error(`${LOG_PREFIX} startSDK status ${status}`));
          return;
        }

        AppData?.log.debug(`${LOG_PREFIX} startSDK response`, response);
        resolve();
      });
    });
  }

  private isStoredJwtValid(
    stored: OneTrustJwtStorage,
    uid: string,
    keyId: string,
    expirationSecs: number,
  ): boolean {
    if (stored.uid !== uid) {
      AppData?.log.debug(`${LOG_PREFIX} stored JWT is for another user`);
      return false;
    }

    if (stored.kid !== keyId) {
      AppData?.log.debug(
        `${LOG_PREFIX} stored JWT key rotated (${stored.kid} -> ${keyId})`,
      );
      return false;
    }

    let exp: number;
    try {
      exp = jwtDecode<JwtClaims>(stored.jwt).exp;
    } catch (e) {
      AppData?.log.debug(`${LOG_PREFIX} stored JWT undecodable`, e);
      return false;
    }

    // Whichever comes first: the token's own `exp`, or the current config
    // offset from when it was generated (the offset may have shrunk since)
    const expiresAt = Math.min(
      exp * 1000,
      stored.generatedAt + expirationSecs * 1000,
    );

    const isValid = Date.now() < expiresAt - JWT_EXPIRY_BUFFER_MS;
    if (!isValid) {
      AppData?.log.debug(
        `${LOG_PREFIX} stored JWT expired`,
        new Date(expiresAt).toISOString(),
      );
    }

    return isValid;
  }

  /**
   * Reuses the stored JWT while it is valid for this uid and key, otherwise
   * requests a new one. Resolves `null` when cross-device isn't configured.
   */
  private async getJwt(uid: string): Promise<string | null> {
    const config = await getConfig();
    const { key, key_id, expiration_time_secs } =
      config.privacy.one_trust ?? {};
    const expirationSecs = Number(expiration_time_secs);

    if (!key || !key_id || !isFinite(expirationSecs) || expirationSecs <= 0) {
      AppData?.log.debug(`${LOG_PREFIX} cross-device consent not configured`);
      return null;
    }

    const storage = AppData!.storageService.oneTrustJwt;
    const stored = storage.get();
    if (stored && this.isStoredJwtValid(stored, uid, key_id, expirationSecs)) {
      AppData?.log.debug(`${LOG_PREFIX} reusing stored JWT`, {
        kid: stored.kid,
        generatedAt: new Date(stored.generatedAt).toISOString(),
      });
      return stored.jwt;
    }

    if (!stored) AppData?.log.debug(`${LOG_PREFIX} no stored JWT`);

    const jwt = await getOneTrustJwt(uid);
    const { kid } = jwtDecode<{ kid: string }>(jwt, { header: true });
    const { iat, exp } = jwtDecode<JwtClaims>(jwt);

    storage.set({ jwt, uid, kid, generatedAt: iat * 1000 });
    AppData?.log.debug(`${LOG_PREFIX} created and stored JWT`, {
      kid,
      issuedAt: new Date(iat * 1000).toISOString(),
      expiresAt: new Date(exp * 1000).toISOString(),
    });

    return jwt;
  }

  private async getSyncHeaders(
    uid: string,
  ): Promise<Partial<OneTrustHeaders> | null> {
    try {
      const jwt = await this.getJwt(uid);
      if (!jwt) return null;

      return {
        'OT-Fetch-Type': FETCH_TYPE_SYNC_PROFILE,
        'OT-Sync-Profile-Auth': jwt,
        'OT-Identifier': uid,
      };
    } catch (error) {
      AppData?.log.debug(`${LOG_PREFIX} cross-device JWT unavailable`, error);
      reportError({ error, userFacing: false, code: ErrorCode.c47 });
      return null;
    }
  }

  private getAnonymousId(): string {
    const advertisingId = this.deviceIntegration.getAdvertisingId();
    if (advertisingId) return advertisingId;

    const storage = AppData!.storageService.oneTrustAnonymousId;
    const saved = storage.get();
    if (saved) return saved;

    const generated = generateUUIDv4();
    storage.set(generated);
    AppData?.log.debug(`${LOG_PREFIX} generated anonymous id`);

    return generated;
  }

  private getConsents(sdk: OneTrustSdk): OneTrustPurposeConsent[] | null {
    const result = sdk.getPurposeConsentStatus();
    if (!Array.isArray(result)) return null;

    const chosen = result.filter(({ status }) => status === 1 || status === 0);
    return chosen.length ? chosen : null;
  }

  // The next profile starts without consent, so the previous one's is restored
  private takePreviousConsents(
    sdk: OneTrustSdk,
    nextIdentifier: string,
  ): OneTrustPurposeConsent[] | null {
    const previous =
      AppData!.storageService.oneTrustIdentifier.get() ??
      sdk.getCurrentActiveProfile();
    if (!previous || previous === nextIdentifier) return null;

    const consents = this.getConsents(sdk);
    const result = sdk.deleteProfile(previous);
    AppData?.log.debug(`${LOG_PREFIX} left profile`, result, consents);

    return consents;
  }

  private waitForConsentSave(previousConsentString: string | null) {
    return new Promise<void>(resolve => {
      const startedAt = Date.now();
      const intervalId = Registry.setInterval(() => {
        const isSaved =
          this.readSdkStorage(CONSENT_STRING_STORAGE_KEY) !==
          previousConsentString;
        const isTimedOut =
          Date.now() - startedAt >= constants.oneTrust.initTimeoutMs;
        if (!isSaved && !isTimedOut) return;

        Registry.clearInterval(intervalId);
        AppData?.log.debug(`${LOG_PREFIX} restored consent`, { isSaved });
        resolve();
      }, SAVE_POLL_INTERVAL_MS);
    });
  }

  private restoreConsents(
    sdk: OneTrustSdk,
    consents: OneTrustPurposeConsent[],
  ): Promise<void> {
    const consentString = this.readSdkStorage(CONSENT_STRING_STORAGE_KEY);

    consents.forEach(({ id, status }) => {
      sdk.updatePurposeConsent(id, status === 1);
    });
    sdk.saveConsent('PREFERENCE_CENTER_CONFIRM');

    return this.waitForConsentSave(consentString);
  }

  private async startAnonymous(
    sdk: OneTrustSdk,
    baseHeaders: OneTrustHeaders,
    previousConsents: OneTrustPurposeConsent[] | null = null,
  ): Promise<void> {
    const anonymousId = this.getAnonymousId();
    const consents =
      previousConsents ?? this.takePreviousConsents(sdk, anonymousId);

    AppData?.log.debug(`${LOG_PREFIX} starting anonymously`);
    await this.withTimeout(
      this.startSdk(sdk, { ...baseHeaders, 'OT-Identifier': anonymousId }),
    );
    AppData!.storageService.oneTrustIdentifier.set(anonymousId);
    if (consents) await this.restoreConsents(sdk, consents);
  }

  private async startSignedIn(
    sdk: OneTrustSdk,
    baseHeaders: OneTrustHeaders,
    uid: string,
    sequence: number,
  ): Promise<boolean> {
    const syncHeaders = await this.getSyncHeaders(uid);
    if (sequence !== this.userSequence) return false;

    if (!syncHeaders) {
      await this.startAnonymous(sdk, baseHeaders);
      return true;
    }

    const previousConsents = this.takePreviousConsents(sdk, uid);

    try {
      AppData?.log.debug(`${LOG_PREFIX} starting with cross-device JWT`);
      await this.withTimeout(
        this.startSdk(sdk, { ...baseHeaders, ...syncHeaders }),
      );
      AppData?.log.debug(`${LOG_PREFIX} cross-device JWT accepted`);
    } catch (error) {
      AppData?.log.debug(`${LOG_PREFIX} cross-device start failed`, error);
      reportError({ error, userFacing: false, code: ErrorCode.c47 });

      AppData!.storageService.oneTrustJwt.remove();
      AppData?.log.debug(`${LOG_PREFIX} cleared rejected JWT`);

      if (sequence !== this.userSequence) return false;

      await this.startAnonymous(sdk, baseHeaders, previousConsents);
      return true;
    }

    AppData!.storageService.oneTrustIdentifier.set(uid);

    // A synced profile that already holds consent wins over the previous one
    if (previousConsents && sdk.shouldShowBanner() === 1) {
      await this.restoreConsents(sdk, previousConsents);
    }

    return true;
  }

  private startForUser(
    sdk: OneTrustSdk,
    baseHeaders: OneTrustHeaders,
    uid: string | null,
    sequence: number,
  ): Promise<boolean> {
    if (uid) return this.startSignedIn(sdk, baseHeaders, uid, sequence);

    return this.startAnonymous(sdk, baseHeaders).then(() => true);
  }

  private restartForUser(uid: string | null): void {
    const sdk = this.sdk;
    const headers = this.getHeaders();
    if (!sdk || !headers) return;

    const sequence = ++this.userSequence;
    this.isStarting = true;

    this.startForUser(sdk, headers, uid, sequence).then(
      started => {
        if (!started) return;

        this.isStarting = false;
        AppData?.log.debug(`${LOG_PREFIX} restarted for user`);
        this.publishConsent(sdk, false);
        this.scheduleRefetch();
      },
      error => {
        if (sequence !== this.userSequence) return;

        this.isStarting = false;
        AppData?.log.debug(`${LOG_PREFIX} restart failed`, error);
        reportError({ error, userFacing: false, code: ErrorCode.c47 });
        this.scheduleRefetch();
      },
    );
  }

  private clearRefetchTimer(): void {
    if (this.refetchTimeoutId === null) return;

    Registry.clearTimeout(this.refetchTimeoutId);
    this.refetchTimeoutId = null;
  }

  private startRefetchTimer(delayMs: number): void {
    this.clearRefetchTimer();
    this.refetchTimeoutId = Registry.setTimeout(() => {
      this.refetchTimeoutId = null;
      this.refetch();
    }, delayMs);
  }

  private scheduleRefetch(): void {
    const delayMs = constants.oneTrust.refetchIntervalMs;
    this.nextRefetchAt = Date.now() + delayMs;
    AppData?.log.debug(`${LOG_PREFIX} refetch scheduled`, {
      at: new Date(this.nextRefetchAt).toISOString(),
      delayMs,
      backgrounded: this.isBackgrounded,
    });

    if (!this.isBackgrounded) this.startRefetchTimer(delayMs);
  }

  private refetch(): void {
    this.clearRefetchTimer();

    // The in-flight start schedules the next refetch when it settles
    if (this.isStarting) return;

    // Restarting under an open dialog would discard an in-progress choice
    if (this.dialogOpen) {
      AppData?.log.debug(`${LOG_PREFIX} refetch deferred, dialog open`);
      this.refetchPending = true;
      return;
    }

    AppData?.log.debug(`${LOG_PREFIX} refetching consent`);
    this.restartForUser(this.currentUid);
  }

  private runPendingRefetch(): void {
    if (!this.refetchPending) return;

    this.refetchPending = false;
    // The foreground handler sees the passed deadline and refetches then
    if (this.isBackgrounded) return;

    this.refetch();
  }

  private readonly onBackgrounding = (): void => {
    this.isBackgrounded = true;
    this.clearRefetchTimer();
    AppData?.log.debug(`${LOG_PREFIX} backgrounded, refetch timer stopped`);
  };

  private readonly onForegrounding = (): void => {
    this.isBackgrounded = false;
    if (this.isStarting || this.nextRefetchAt === null) return;

    const remainingMs = this.nextRefetchAt - Date.now();
    AppData?.log.debug(`${LOG_PREFIX} foregrounded`, { remainingMs });

    if (remainingMs <= 0) {
      this.refetch();
    } else {
      this.startRefetchTimer(remainingMs);
    }
  };

  private withTimeout<T>(promise: Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timeoutId = Registry.setTimeout(() => {
        reject(
          new Error(
            `${LOG_PREFIX} timed out after ${constants.oneTrust.initTimeoutMs}ms`,
          ),
        );
      }, constants.oneTrust.initTimeoutMs);

      promise.then(
        value => {
          Registry.clearTimeout(timeoutId);
          resolve(value);
        },
        error => {
          Registry.clearTimeout(timeoutId);
          reject(error);
        },
      );
    });
  }

  private readTargetingConsent(sdk: OneTrustSdk): OneTrustPurposeStatus | null {
    const result = sdk.getPurposeConsentStatus(TARGETING_PURPOSE_ID);
    if (!Array.isArray(result)) {
      AppData?.log.debug(`${LOG_PREFIX} consent unavailable`, result);
      return null;
    }

    const status = result[0]?.status;
    return status === 1 || status === 0 ? status : -1;
  }

  private readSdkStorage(key: string): string | null {
    // Written by the SDK itself, so it bypasses the device storage wrappers
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      AppData?.log.debug(`${LOG_PREFIX} ${key} unreadable`, e);
      return null;
    }
  }

  /**
   * Hands the SDK's current consent to `PrivacyService`. An unreadable read
   * keeps the last snapshot rather than guessing.
   */
  private publishConsent(sdk: OneTrustSdk, isUserChoice: boolean): void {
    const targeting = this.readTargetingConsent(sdk);
    if (targeting === null) return;

    const consent = {
      targeting,
      usPrivacy: this.readSdkStorage(US_PRIVACY_STORAGE_KEY),
      gppString: this.readSdkStorage(GPP_STRING_STORAGE_KEY),
      gppSid: this.readSdkStorage(GPP_SID_STORAGE_KEY),
    };
    AppData?.log.debug(`${LOG_PREFIX} consent read`, consent);

    this.privacyService.setOneTrustConsent(consent, isUserChoice);
  }

  private stopWatchingDialog(): void {
    window.removeEventListener(DIALOG_STATUS_EVENT, this.onDialogStatusChange);
    this.hasDialogOpened = false;

    if (this.dialogTimeoutId !== null) {
      Registry.clearTimeout(this.dialogTimeoutId);
      this.dialogTimeoutId = null;
    }

    this.dialogOpen = false;

    const onBannerClosed = this.onBannerClosed;
    this.onBannerClosed = null;
    onBannerClosed?.();
  }

  /**
   * The SDK reports its dialog opening and closing through a window event, but
   * only dispatches on a status change, so a dialog that never renders may send
   * nothing. The timeout keeps that from stranding input.
   */
  private watchDialog(): void {
    window.addEventListener(DIALOG_STATUS_EVENT, this.onDialogStatusChange);

    this.dialogTimeoutId = Registry.setTimeout(() => {
      if (this.hasDialogOpened) return;

      AppData?.log.debug(`${LOG_PREFIX} dialog never opened`);
      this.stopWatchingDialog();
      this.runPendingRefetch();
    }, constants.oneTrust.initTimeoutMs);
  }

  /**
   * Suspends app input before showing, so the press that opened the dialog
   * can't also land on the page behind it.
   */
  private beginDialog(): void {
    ensureOneTrustAboveCanvas();

    this.dialogOpen = true;
    this.watchDialog();
  }

  private readonly onDialogStatusChange = (event: Event): void => {
    const { detail } = event as CustomEvent<OneTrustDialogStatusDetail>;

    if (detail?.status) {
      this.hasDialogOpened = true;
      return;
    }

    // Closing without ever opening (e.g. offline) is not a choice
    const isUserChoice = this.hasDialogOpened;
    this.stopWatchingDialog();

    AppData?.log.debug(`${LOG_PREFIX} dialog closed`, detail);
    if (isUserChoice && this.sdk) this.publishConsent(this.sdk, true);
    this.runPendingRefetch();
  };

  /** Opens the OneTrust preference center. No-op until the SDK is up. */
  showPreferenceCenter(): void {
    if (!this.sdk) {
      AppData?.log.debug(`${LOG_PREFIX} not initialized, cannot show the PC`);
      return;
    }

    if (this.dialogOpen) return;

    this.beginDialog();
    this.sdk.showPreferencesUI('self');
  }

  showBannerIfRequired(): Promise<void> {
    const sdk = this.sdk;
    if (!sdk) return Promise.resolve();

    // Returned Values:
    // -1 - SDK not initialized
    // 1 - Banner should be shown
    // 0 - Banner should not be shown
    const shouldShowBanner = sdk.shouldShowBanner();
    AppData?.log.debug(`${LOG_PREFIX} shouldShowBanner`, shouldShowBanner);

    if (this.dialogOpen) return Promise.resolve();
    if (shouldShowBanner !== 1 && !this.config.forceBanner) {
      return Promise.resolve();
    }

    return new Promise<void>(resolve => {
      this.onBannerClosed = resolve;
      this.beginDialog();

      let isShown: boolean | void = false;
      try {
        isShown = sdk.showBannerUI('self');
      } catch (error) {
        reportError({ error, userFacing: false, code: ErrorCode.c47 });
      }

      if (isShown !== true) {
        AppData?.log.debug(`${LOG_PREFIX} banner unavailable`, isShown);
        this.stopWatchingDialog();
      }
    });
  }

  async init(backgroundingService: BackgroundingService): Promise<void> {
    if (this.isInitialized) return;

    if (!this.deviceIntegration.canUseOneTrustSdk()) return;

    const headers = this.getHeaders();
    if (!headers) return;

    const path = getOneTrustSdkPath(this.deviceIntegration.getPlatform());
    if (!path) {
      AppData?.log.debug(`${LOG_PREFIX} no SDK package for this platform`);
      return;
    }

    const uid = AppData?.loginRadiusService?.getAccount()?.uid ?? null;
    this.currentUid = uid;
    const sequence = ++this.userSequence;

    try {
      const sdk = await this.withTimeout(loadOneTrustScript(path));
      const started = await this.startForUser(sdk, headers, uid, sequence);

      this.sdk = sdk;
      this.sdk.setRemoteKeys(this.deviceIntegration.getOneTrustKeyMapping());
      AppData?.log.debug(`${LOG_PREFIX} initialized`);
      // When superseded, the restart below publishes instead
      if (started) {
        this.publishConsent(sdk, false);
        this.scheduleRefetch();
      }
    } catch (error) {
      AppData?.log.debug(`${LOG_PREFIX} initialization failed`, error);
      reportError({ error, userFacing: false, code: ErrorCode.c47 });
      return;
    }

    backgroundingService.addBackgroundingAction(
      'OneTrustServiceBackgrounding',
      this.onBackgrounding,
    );
    backgroundingService.addForegroundingAction(
      'OneTrustServiceForegrounding',
      this.onForegrounding,
    );

    // The user signed in or out while we were starting up
    if (this.currentUid !== uid) this.restartForUser(this.currentUid);
  }

  /**
   * Called on every login and logout. Fire-and-forget: restarts the SDK with
   * (or without) cross-device headers, and never blocks the auth flow.
   */
  setUser(uid: string | null): void {
    // Always clear on logout, even before init or when already signed out
    if (!uid) {
      AppData?.storageService.oneTrustJwt.remove();
      AppData?.log.debug(`${LOG_PREFIX} signed out, cleared stored JWT`);
    }

    if (uid === this.currentUid) return;
    this.currentUid = uid;

    // Not started yet, `init()` picks up the current user itself
    if (!this.sdk) return;

    this.restartForUser(uid);
  }
}
